import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';

interface TraceRow {
  readonly traceabilityId: string;
  readonly traceCode: string;
  readonly requirementId: string;
  readonly featureId: string | null;
  readonly traceStage: string;
  readonly status: string;
}

const STAGES = ['', 'REQUIREMENT', 'FEATURE', 'DESIGN', 'DEVELOPMENT', 'TESTING', 'RELEASE', 'PRODUCTION'];
const STATUSES = ['', 'NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'BLOCKED'];

/**
 * M33 — Requirement Traceability Matrix (§31 / §10). End-to-end coverage grid
 * of traceability links (T437) mapping each requirement across the seven
 * delivery stages (REQUIREMENT→PRODUCTION). Filterable by stage and link
 * status so BR-03 traceability gaps surface before release.
 */
export function RequirementTraceabilityMatrix(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<TraceRow[]>([]);
  const [traceStage, setTraceStage] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (traceStage) params.set('traceStage', traceStage);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: TraceRow[] }>(`/v1/pm/traceability${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, traceStage, status]);

  return (
    <main aria-label="requirement-traceability-matrix">
      <h1>{t('m33.trace.title')}</h1>
      <div className="filters">
        <label>
          {t('m33.trace.stage')}
          <select aria-label="filter-trace-stage" value={traceStage} onChange={(e) => setTraceStage(e.target.value)}>
            {STAGES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m33.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m33.common.loading')}</p> : (
        <>
          <p aria-label="trace-count">{t('m33.trace.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="trace-table"
            rows={rows}
            rowKey={(r) => r.traceabilityId}
            columns={[
              { key: 'traceCode', header: t('m33.common.code'), render: (r) => r.traceCode },
              { key: 'requirementId', header: t('m33.trace.requirement'), render: (r) => r.requirementId },
              { key: 'featureId', header: t('m33.trace.feature'), render: (r) => r.featureId ?? '—' },
              { key: 'traceStage', header: t('m33.trace.stage'), render: (r) => r.traceStage },
              { key: 'status', header: t('m33.common.status'), render: (r) => r.status },
            ]}
          />
        </>
      )}
    </main>
  );
}
